import React, { useEffect, useMemo, useRef, useState } from "react";

import { useForm, useFieldArray } from "react-hook-form";
import {
  RideButton,
  RideGlAddSolid,
  RideGlInformationOutline,
} from "@rimac-seguros/ride-system-components";
import { useDispatch, useSelector } from "react-redux";
import Lottie from "lottie-react";

import { Modal } from "@/components/molecules/NewModal";
import { CardFormBeneficiary } from "./CardFormBeneficiary";
import {
  showModalAddBeneficiaries,
  updateBeneficiaries,
} from "@/redux/actions/beneficiaries";
import { FooterAction } from "./FooterAction";
import useBreakpoint from "@/hooks/useBreakpoint";
import { useGetRelationship } from "@/hooks/useGetRelationship";
import { DOCUMENT_TYPE_DNI } from "@/constants/list-document-type";
import { setErrorPercentage } from "@/utils/beneficiary-utils";
import congratulationsLottie from "@/assets/animation/congratulations.json";
import { APP_KEY_ENCRYPT } from "@/utils/awsConfig";
import { encrypt_AES256_2 } from "@/utils/CryptographyUtils";
import helpers from "@/utils/helpers";

import "./styles.scss";

const supportEmail = 'atencionalcliente@rimac.com.pe';

export const ModalAddBeneficiaries = ({
  isOpen,
  nextStep,
  stepFinish,
  product,
}) => {
  const { beneficiaries } = useSelector((state) => state.beneficiaries);
  const { userData } = useSelector((state) => state.vida);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLottie, setShowLottie] = useState(false);
  const [savingCount, setSavingCount] = useState(0);

  const isAnyCardSaving = savingCount > 0;

  const handleSavingChange = (isSaving) => {
    setSavingCount((prev) => Math.max(0, prev + (isSaving ? 1 : -1)));
  };

  const {
    register,
    watch,
    formState: { errors },
    control,
    getValues,
    trigger,
    setValue,
    setError,
    resetField,
    clearErrors,
    getFieldState,
  } = useForm({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      beneficiaries: [],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "beneficiaries",
  });

  const dispatch = useDispatch();
  useGetRelationship();

  const { pathname } = window.location;
  const isVCD = pathname.includes("seguro-vida-devolucion-garantizada");
  const isVidaPlus = pathname.includes("seguro-vida-plus");
  const isVE = helpers.validateVidaEndosable();

  const contentRef = useRef();
  const lottieRef = useRef();
  const isDesktop = useBreakpoint("large-min");
  const isTablet = useBreakpoint("tablet");

  useEffect(() => {
    if (isOpen) {
      if (beneficiaries.length) {
        beneficiaries.forEach((beneficiary, i) => {
          append(beneficiary);
          setTimeout(() => {
            trigger(`beneficiaries.${i}.percentage`);
          }, 100);
        });
      } else {
        append({
          documentType: DOCUMENT_TYPE_DNI.value,
          streetType: "AV",
          status: 0,
        });
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (lottieRef.current) {
      lottieRef.current.setSpeed(1.5);
    }
  }, [lottieRef]);

  const percentComplete = useMemo(() => {
    const total = getValues("beneficiaries")
      .filter((_) => _.status === 1)
      .reduce((acc, field) => {
        const percentage = Number(field.percentage) || 0;
        return acc + percentage;
      }, 0);

      return isNaN(total) ? 0 : total;
  }, [getValues()]);

  useEffect(() => {
    if (percentComplete === 100) {
      setShowLottie(true);

      const timeout = setTimeout(() => {
        setShowLottie(false);
      }, 4000);

      return () => clearTimeout(timeout);
    }
    setShowLottie(false);
  }, [percentComplete]);

  useEffect(() => {
    if (percentComplete > 100) {
      setErrorPercentage(setError, getValues);
    }
  }, [percentComplete, getValues]);

  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const scrollThreshold = window.innerWidth >= 1024 ? 136 : 56;
        setIsScrolled(contentRef.current.scrollTop >= scrollThreshold);
      }
    };

    const div = contentRef.current;
    if (div) {
      div.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (div) {
        div.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const handleClickAddBeneficiary = () => {
    append({
      documentType: DOCUMENT_TYPE_DNI.value,
      streetType: "AV",
      status: 0,
    });
    setTimeout(() => {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 200);

    helpers.setActionTag({
      group: product,
      category: isVCD || isVE ? "Elegir beneficiarios" : "Interacciones",
      action: isVCD || isVE ? "click" : "Click",
      label: isVCD || isVE
        ? "Agregar beneficiario"
        : "Elegir beneficiarios – Agregar beneficiario",
    });
  };

  const onHandleRemove = (index) => {
    if (isAnyCardSaving) return;

    remove(index);

    if (beneficiaries[index]) {
      const list = beneficiaries.filter((_, i) => i !== index);
      dispatch(updateBeneficiaries(list));
    }

    if (fields.length === 1) {
      handleClickAddBeneficiary();
    }

    const currentBeneficiaries = watch("beneficiaries");
    const currentErrors = getFieldState("beneficiaries").error;
    const currentTotalPercentage = currentBeneficiaries.filter(_ => _.status === 1).reduce(
      (acc, field) => acc + Number(field.percentage),
      0
    );

    if (currentErrors && currentTotalPercentage < 100) {
      currentErrors.forEach((error, idx) => {
        if (error?.percentage?.type === "invalidExceeded") {
          clearErrors(`beneficiaries.${idx}`);
          trigger(`beneficiaries.${idx}.percentage`);
        }
      });
    }

    helpers.setActionTag({
      group: product,
      category: isVCD || isVE ? "Elegir beneficiarios" : "Interacciones",
      action: isVCD || isVE ? "click" : "Click",
      label: isVCD || isVE
        ? "Eliminar beneficiario"
        : "Elegir beneficiarios – Eliminar beneficiario",
    });
  };

  const disabledAddBeneficiary = useMemo(() => {
    return (
      getValues("beneficiaries").some((_) => _.status === 0) ||
      percentComplete >= 100
    );
  }, [getValues(), percentComplete]);

  const handleClickLink = (link) => {
    if (userData.indPrevPageApp) {
      const email = link.replace("mailto:", "");
      const message = `EMBEDDED-APP-HIPERVINCULO : {"accion": "EMAIL", "valor": "${email}", "mensaje": ""}`;
      const encryptedMessage = encrypt_AES256_2(message, APP_KEY_ENCRYPT);
      return helpers.sendMessageToApp(encryptedMessage);
    }
    window.open(link, "_blank", "noopener noreferrer");
  };

  const handleCloseModal = () => {
    dispatch(showModalAddBeneficiaries(false));

    if (isVE) {
      helpers.setActionTag({
        group: product,
        category: "Elegir beneficiarios",
        action: "click",
        label: "Cerrar"
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      fullContent
      iconCloseOutside={isTablet}
      className="modal-ab col-span-full md:col-span-8 md:col-start-3"
      classNameContent="modal-ab_content"
      classNameContentHead={isScrolled ? "modal-ab_head" : ""}
      ref={contentRef}
      onClose={handleCloseModal}
    >
      <div className="modal-ab_full mt-ride-gl-spacing-8xlarge lg:mt-ride-gl-spacing-9xlarge">
        <h1 className={isScrolled ? "scrolled" : ""}>
          Elige tus <span>beneficiarios</span>
        </h1>
        <div className={`modal-ab_percentage ${isScrolled ? "scrolled" : ""}`}>
          <div className="relative flex items-center justify-between mb-ride-gl-spacing-large lg:mb-ride-gl-spacing-xlarge">
            <p className="ride-sys-text-label-medium-regular">
              <span>Suma total de</span>{" "}
              <span className="ride-sys-text-label-medium-default">
                porcentajes
              </span>{" "}
              👉
            </p>
            <span className="ride-sys-text-label-large-default lg:ride-sys-text-label-xlarge-default">
              {percentComplete > 100 ? 100 : percentComplete}%
            </span>
            {showLottie && (
              <div className="lottie-congratulations">
                <Lottie
                  lottieRef={lottieRef}
                  animationData={congratulationsLottie}
                  autoplay={true}
                />
              </div>
            )}
          </div>
          <div className="relative w-full h-1.5 overflow-hidden bg-ride-sys-color-surface-medium rounded-ride-sys-border-radius-container-small lg:h-2">
            <div
              className="relative h-full overflow-hidden bg-ride-sys-color-chart-01"
              style={{
                width: `${percentComplete === 0 ? 0.5 : percentComplete}%`,
              }}
            >
              <div
                className={`absolute inset-0 ${
                  percentComplete >= 100
                    ? "bg-ride-sys-color-chart-01"
                    : "modal-ab_bar"
                }`}
              ></div>
            </div>
          </div>
        </div>

        <div className="grid w-full gap-ride-gl-spacing-5xlarge lg:gap-ride-gl-spacing-2xlarge">
          {fields.map((field, index) => (
            <CardFormBeneficiary
              key={field.id}
              index={index}
              register={register}
              watch={watch}
              errors={errors}
              getValues={getValues}
              trigger={trigger}
              setValue={setValue}
              setError={setError}
              resetField={resetField}
              clearErrors={clearErrors}
              isVCD={isVCD}
              product={product}
              onHandleRemove={() => onHandleRemove(index)}
              getFieldState={getFieldState}
              onSavingChange={handleSavingChange}
              isAnyCardSaving={isAnyCardSaving}
            />
          ))}
        </div>

        <RideButton
          variant="text-primary"
          glyph={<RideGlAddSolid key="RideGlAddSolid" />}
          glyphPosition="left"
          text="Agregar beneficiario"
          size={isDesktop ? "large" : "medium"}
          onClick={handleClickAddBeneficiary}
          disabled={disabledAddBeneficiary}
          className={`${
            getValues("beneficiaries").some((_) => _.status === 0)
              ? "mt-ride-gl-spacing-5xlarge"
              : "mt-ride-gl-spacing-4xlarge"
          } lg:mt-ride-gl-spacing-2xlarge`}
        />

        <div className="flex border-t gap-ride-gl-spacing-large pt-ride-gl-spacing-5xlarge mt-ride-gl-spacing-7xlarge border-ride-sys-color-border-default lg:pt-ride-gl-spacing-4xlarge lg:mt-ride-gl-spacing-6xlarge">
          <RideGlInformationOutline size="large" />
          <p className="ride-sys-text-paragraph-small-default text-ride-sys-color-text-medium">
            Asegúrate de ingresar los datos correctos para comunicarnos con tus
            beneficiarios. Si no tienes la información, regresa y selecciona
            “Herederos legales”. Podrás modificar a tus beneficiarios en
            cualquier momento enviando un correo a{" "}
            {
              isVE ?
                <b>{supportEmail}</b> :
                <button
                  type="button"
                  className="cursor-pointer ride-sys-text-paragraph-small-underline"
                  onClick={() =>
                    handleClickLink(`mailto:${supportEmail}`)
                  }
                >
                  {supportEmail}
                </button>
            }
          </p>
        </div>
      </div>
      <FooterAction
        disabled={!(percentComplete === 100 && !Object.keys(errors).length)}
        nextStep={nextStep}
        stepFinish={stepFinish}
        isVidaPlus={isVidaPlus}
      />
    </Modal>
  );
};
